<template>
  <PageBreadcrumb
    :crumbs="[{
      label: 'Gestione Aste',
      link: '/aste'
    }, {
      label: form.uuid ? 'Modifica Asta' : 'Aggiungi nuova Asta'
    }]"
  />

  <form autocomplete="off">
    <InputText
      v-model="form.name"
      :error-fields="errorFields"
      name="name"
      :submitted="submitted"
    >
      Nome
    </InputText>

    <InputSelect
      v-model="form.auctionType"
      :disabled="form.uuid !== undefined"
      :error-fields="errorFields"
      name="auctionType"
      :options="Object.entries(getAuctionTypeAllLabels()).map(([key, value]) => ({
        id: key,
        label: value
      }))"
      :submitted="submitted"
    >
      Tipologia
    </InputSelect>

    <DateTimeInput
      v-model="form.startAt"
      :error-fields="errorFields"
      name="startAt"
      :submitted="submitted"
    >
      Data Inizio
    </DateTimeInput>

    <DateTimeInput
      v-model="form.endAt"
      :error-fields="errorFields"
      name="endAt"
      :submitted="submitted"
    >
      Data Fine
    </DateTimeInput>

    <CategoryInputSelect
      v-model="form.categoryUuid"
      :disabled="form.uuid !== undefined"
      :error-fields="errorFields"
      name="categoryUuid"
      :submitted="submitted"
    >
      Categoria
    </CategoryInputSelect>
    
    <div
      v-if="form.auctionType == 'REDISTRIBUTE'"
      class="flex flex-wrap mb-4"
    >
      <label
        class="w-1/4"
      >
        Quantità minime per prodotto
      </label>
      <div class="flex-grow">
        <div class="flex flex-col">
          <div
            v-for="product in products"
            :key="'product-redistribute-' + product.uuid"
          >
            <InputInteger 
              v-model="form.qmgs[product.uuid]"
              :error-fields="errorFields"
              :name="'qmgs_'+product.uuid"
              :submitted="submitted"
            >
              {{ product.code }}
            </InputInteger>
          </div>
        </div>
      </div>
    </div>

    <InputInteger
      v-if="form.auctionType == 'REDISTRIBUTE'"
      v-model="redistributablePeriodMinutesPhase1"
      :error-fields="errorFields"
      name="redistributablePeriodMillisPhase1"
      :submitted="submitted"
    >
      Durata dello stato Redistribuzione<br><span class="text-sm">(fase 1 - in minuti)</span>
    </InputInteger>

    <InputInteger
      v-if="form.auctionType == 'REDISTRIBUTE'"
      v-model="redistributablePeriodMinutesPhase2"
      :error-fields="errorFields"
      name="redistributablePeriodMillisPhase2"
      :submitted="submitted"
    >
      Durata dello stato Redistribuzione<br><span class="text-sm">(fase 2 - in minuti)</span>
    </InputInteger>

    <InputBoolean
      v-model="form.groupsEnabled"
      :error-fields="errorFields"
      name="groupsEnabled"
      :submitted="submitted"
    >
      Gestione Gruppi attiva
    </InputBoolean>

    <SubmitButtons
      :create-and-back="createAndBack"
      :create-and-stay="createAndStay"
      :mode="form.uuid ? 'update' : 'create'"
      :pending="pending"
      :update-and-back="updateAndBack"
      :update-and-stay="updateAndStay"
    />
  </form>
</template>

<script setup>
import moment from 'moment';
import { reactive, ref,watch } from "vue";

import CategoryInputSelect from "@/components/form/CategoryInputSelect.vue";
import DateTimeInput from "@/components/form/DateTimeInput.vue";
import InputBoolean from "@/components/form/InputBoolean.vue";
import InputInteger from '@/components/form/InputInteger.vue';
import InputSelect from "@/components/form/InputSelect.vue";
import InputText from "@/components/form/InputText.vue";
import SubmitButtons from "@/components/form/SubmitButtons.vue";
import PageBreadcrumb from "@/components/PageBreadcrumb.vue";
import useAxios from "@/composables/axios";
import useEnums from '@/composables/enums.js'
import useForm from "@/composables/form";

const props = defineProps({
  id: {
    required: false,
    type: String,
    default: null
  },
});

const axios = useAxios();
const { getAuctionTypeAllLabels } = useEnums()

let form = reactive({
  name: "",
  categoryUuid: null,
  startAt: null,
  endAt: null,
  auctionType: 'STANDARD',
  redistributablePeriodMillisPhase1: null,
  redistributablePeriodMillisPhase2: null,
  groupsEnabled: false,
  qmgs: {}

  // closingPeriodMillis: 0, //TBD
  // extensionPeriodMillis: 0, //TBD
  // minBid: 0, //TBD
});

const redistributablePeriodMinutesPhase1 = ref(null)
watch(redistributablePeriodMinutesPhase1, (value) => {
  form.redistributablePeriodMillisPhase1 = value*60*1000;
})

const redistributablePeriodMinutesPhase2 = ref(null)
watch(redistributablePeriodMinutesPhase2, (value) => {
  form.redistributablePeriodMillisPhase2 = value*60*1000;
})

if (props.id) {
  const resp = await axios.get("/auction/" + props.id);
  form = reactive(resp.data);
  if(form.qmgs === undefined) {
    form.qmgs = {}
  }
  redistributablePeriodMinutesPhase1.value = resp.data.redistributablePeriodMillisPhase1 ? resp.data.redistributablePeriodMillisPhase1 / 60 / 1000 : null;
  redistributablePeriodMinutesPhase2.value = resp.data.redistributablePeriodMillisPhase2 ? resp.data.redistributablePeriodMillisPhase2 / 60 / 1000 : null;
}

const allProductsResp = await axios.get("/product/list");
const allProducts = ref(allProductsResp.data);
const products = ref([]);
const syncProducts = (value, setup) => {
  products.value = allProducts.value.filter(product => product.categoryUuid == value);
  if(!setup) {
    form.qmgs = {};
  }
}
watch(() => form.categoryUuid, (value) => syncProducts(value, false))
if(form.categoryUuid) {
  syncProducts(form.categoryUuid, true);
}

const rules = {
  name: {
    type: "string",
    max: 128,
    required: true,
  },
  auctionType: {
    type: "string",
    required: true,
  },
  categoryUuid: {
    type: "string",
    required: true,
  },
  startAt: {
    type: "date",
    required: true,
  },
  endAt: [{
    type: "date",
    required: true,
  }, {
    validator(rule, value, callback, source, options) {
      if(value && source.startAt) {
        const endDate = moment(value)
        const startDate = moment(source.startAt)
        if(!endDate.isAfter(startDate)) {
          return ['Data Fine precedente a Data Inizio'];
        }
      }
      return [];
    }
  }],
  redistributablePeriodMillisPhase1: {
    type: 'integer',
    validator: (rule, value, callback, source) => {
      if (source.auctionType == 'REDISTRIBUTE' && !value) {
        return ['Campo obbligatorio'];
      } else if (source.auctionType != 'REDISTRIBUTE' && value) {
        return ['Campo obbligatorio solo se tipologia redistributiva'];
      }
      return true;
    },
  },
  redistributablePeriodMillisPhase2: {
    type: 'integer',
    validator: (rule, value, callback, source) => {
      if (source.auctionType == 'REDISTRIBUTE' && !value) {
        return ['Campo obbligatorio'];
      } else if (source.auctionType != 'REDISTRIBUTE' && value) {
        return ['Campo obbligatorio solo se tipologia redistributiva'];
      }
      return true;
    },
  },
  qmgs: {
    validator: (rule, value, callback, source) => {
      if (source.auctionType == 'REDISTRIBUTE') {
        const qmgsWithError = [];
        for (const key in value) {
          if (value[key] == null) {
            qmgsWithError.push(key);
          }
        }
        if (qmgsWithError.length > 0) {
          return qmgsWithError.map((qmgs) => {
            return {
              field: 'qmgs_' + qmgs,
              message: "Quantità non valida"
            }
          });
        }
      }

      return true;
    }
  }
  // closingPeriodMillis: {
  //   type: "number",
  //   min: 0
  // },
  // extensionPeriodMillis: {
  //   type: "number",
  //   min: 0
  // },
  // minBid: {
  //   type: "number",
  //   min: 0,
  //   required: true
  // }
};

const { submitted, pending, errorFields, createAndBack, createAndStay, updateAndBack, updateAndStay } = useForm(form, 'auction', rules, {
  create: 'Asta creata con successo',
  update: 'Asta modificata con successo'
}, '/aste', () => {
  form.name = "";
  form.categoryUuid = null;
  form.startAt = null;
  form.endAt = null;
  form.auctionType = 'STANDARD';
  form.redistributablePeriodMillisPhase1 = null;
  form.redistributablePeriodMillisPhase2 = null;
  form.groupsEnabled = false;
  form.qmgs = {};
  redistributablePeriodMinutesPhase1.value = null;
  redistributablePeriodMinutesPhase2.value = null;

  // form.closingPeriodMillis = 0; //TBD
  // form.extensionPeriodMillis = 0; //TBD
  // form.minBid = 0; //TBD
})

</script>
